'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { isAppRole, type AppRole } from '@/lib/auth/types';
import { roleFromMetadata } from '@/lib/auth/utils';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: AppRole | null;
  roleResolved: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, unknown>) => Promise<unknown>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  isEmailVerified: () => boolean;
  getUserRole: () => AppRole | null;
  getUserProfile: () => Promise<Record<string, unknown> | null>;
  resolveRole: (userOverride?: User | null) => Promise<AppRole | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

function siteOrigin() {
  return process.env.NEXT_PUBLIC_SITE_URL || 'https://maximumeffortmdj.com';
}

function sameUser(a: User | null, b: User | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.last_sign_in_at === b.last_sign_in_at;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [roleResolved, setRoleResolved] = useState(false);
  const [loading, setLoading] = useState(true);

  const supabase = useMemo(() => createClient(), []);

  const resolveRole = useCallback(
    async (userOverride?: User | null): Promise<AppRole | null> => {
      const target = userOverride === undefined ? user : userOverride;
      if (!target) return null;

      const fromMeta = roleFromMetadata(target);
      if (fromMeta) return fromMeta;

      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', target.id)
        .maybeSingle();

      return isAppRole(data?.role) ? data.role : null;
    },
    [supabase, user]
  );

  useEffect(() => {
    let cancelled = false;

    const syncRole = async (nextUser: User | null) => {
      if (!nextUser) {
        if (!cancelled) {
          setRole(null);
          setRoleResolved(true);
        }
        return;
      }

      // Prefer metadata synchronously to avoid auth-gate flicker
      const fromMeta = roleFromMetadata(nextUser);
      if (fromMeta) {
        if (!cancelled) {
          setRole(fromMeta);
          setRoleResolved(true);
        }
        return;
      }

      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', nextUser.id)
        .maybeSingle();

      if (!cancelled) {
        setRole(isAppRole(data?.role) ? data.role : null);
        setRoleResolved(true);
      }
    };

    const applySession = (nextSession: Session | null) => {
      if (cancelled) return;
      const nextUser = nextSession?.user ?? null;
      setSession(nextSession);
      setUser((prev) => (sameUser(prev, nextUser) ? prev : nextUser));
      void syncRole(nextUser);
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      applySession(nextSession);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      applySession(nextSession);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
    // Intentionally stable: do not depend on resolveRole/user (that caused an infinite resubscribe loop)
  }, [supabase]);

  const signUp = useCallback(
    async (email: string, password: string, metadata: Record<string, unknown> = {}) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: (metadata?.fullName as string) || '',
            avatar_url: (metadata?.avatarUrl as string) || '',
            role: (metadata?.role as string) || 'client',
            company: (metadata?.company as string) || '',
          },
          emailRedirectTo: `${siteOrigin()}/auth/callback`,
        },
      });
      if (error) throw error;
      return data;
    },
    [supabase]
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('auth:signed_out');
      }
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    },
    [supabase]
  );

  const resetPassword = useCallback(
    async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteOrigin()}/auth/callback?next=/reset-password`,
      });
      if (error) throw error;
    },
    [supabase]
  );

  const signOut = useCallback(async () => {
    // Clear local auth state first so login page does not auto-redirect
    setUser(null);
    setSession(null);
    setRole(null);
    setRoleResolved(true);
    setLoading(false);

    if (typeof window !== 'undefined') {
      sessionStorage.setItem('auth:signed_out', '1');
    }

    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) {
      // Local session clear is enough for UX; still surface unexpected failures
      const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
      if (localError) throw localError;
    }
  }, [supabase]);

  const getCurrentUser = useCallback(async () => {
    const {
      data: { user: currentUser },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return currentUser;
  }, [supabase]);

  const isEmailVerified = useCallback(() => {
    return user?.email_confirmed_at != null;
  }, [user]);

  const getUserRole = useCallback(() => role, [role]);

  const getUserProfile = useCallback(async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  }, [supabase, user]);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      role,
      roleResolved,
      signUp,
      signIn,
      signOut,
      resetPassword,
      getCurrentUser,
      isEmailVerified,
      getUserRole,
      getUserProfile,
      resolveRole,
    }),
    [
      user,
      session,
      loading,
      role,
      roleResolved,
      signUp,
      signIn,
      signOut,
      resetPassword,
      getCurrentUser,
      isEmailVerified,
      getUserRole,
      getUserProfile,
      resolveRole,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
